// frontend/source/portal/pages/AdminArtworkListPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import adminApi from "../../services/adminapi";
import api from "../../services/api";
import CreateArtworkCard from "../components/CreateArtworkCard";
import EditArtworkCard from "../components/EditArtworkCard";
import ConfirmDialog from "../components/ConfirmDialog";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import "./AdminArtworkListPage.css";

const AdminArtworkListPage = () => {
  const { categoryId, tagId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [artTag, setArtTag] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState({
    isOpen: false,
    artwork: null,
  });

  useEffect(() => {
    loadDetailsAndArtworks();
  }, [categoryId, tagId]);

  const loadDetailsAndArtworks = async () => {
    try {
      setLoading(true);
      const [categoryData, tagData] = await Promise.all([
        api.getCategoryById(categoryId),
        api.getArtTagById(tagId),
      ]);
      setCategory(categoryData);
      setArtTag(tagData);

      const artworksData = await api.getArtworksByTag(categoryId, tagData?._id, 1, 0, false);
      console.log("artworksData", artworksData);
      setArtworks(artworksData.artworks || artworksData);
      setError(null);
    } catch (err) {
      setError("Failed to load artworks. Please try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToArtTags = () => {
    navigate(`/portal/category/${category._id}/tags`);
  };

  const handleRemove = async (artworkId) => {
    const artwork = artworks.find((art) => art._id === artworkId);
    setRemoveConfirm({ isOpen: true, artwork });
  };

  const confirmRemove = async () => {
    try {
      await adminApi.removeArtworkFromTag(
        tagId,
        removeConfirm.artwork._id
      );
      setArtworks(
        artworks.filter((art) => art._id !== removeConfirm.artwork._id)
      );
      setRemoveConfirm({ isOpen: false, artwork: null });
    } catch (err) {
      setError("Failed to remove artwork from tag. Please try again.");
      console.error(err);
      setRemoveConfirm({ isOpen: false, artwork: null });
    }
  };

  const cancelRemove = () => {
    setRemoveConfirm({ isOpen: false, artwork: null });
  };

  const handleUpdate = async (artworkId, updateData) => {
    try {
      const updated = await adminApi.updateArtwork(artworkId, updateData);
      setArtworks(
        artworks.map((art) => (art._id === artworkId ? updated : art))
      );
    } catch (err) {
      setError("Failed to update artwork. Please try again.");
      console.error(err);
      throw err;
    }
  };

  const handleCreate = async (artworkData) => {
    try {
      console.log("handleCreate", artworkData);
      const newArtwork = await adminApi.createArtwork(artworkData);
      // Associate with current tag
      await adminApi.addArtworkToTag(tagId, newArtwork._id);
      setArtworks([newArtwork, ...artworks]);
      setError(null);
    } catch (err) {
      setError("Failed to create artwork. Please try again.");
      console.error(err);
      throw err;
    }
  };

  const handleAddExisting = async (artworkId) => {
    try {
      await adminApi.addArtworkToTag(tagId, artworkId);
      // Reload artworks to include the newly added one
      const artworksData = await api.getArtworksByTag(categoryId, tagId, 1, 0);
      setArtworks(artworksData.artworks || artworksData);
      setError(null);
    } catch (err) {
      setError("Failed to add artwork to tag. Please try again.");
      console.error(err);
      throw err;
    }
  };

    const handleViewDetails = (artworkId) => {
        console.log("opening artworkdetails", categoryId, tagId);
      navigate(`/portal/category/${categoryId}/tag/${tagId}/artwork/${artworkId}`, {
        state: {
          artworkList: artworks,
          categoryName: category?.name,
          tagName: artTag?.name,
        },
      });
    };

  if (loading) {
    return <LoadingSpinner message="Loading artworks..." />;
  }

  if (error && !category) {
    return <ErrorMessage message={error} onRetry={loadDetailsAndArtworks} />;
  }

  return (
    <div className="admin-artwork-list-main">
      <div className="admin-artwork-list-content">
        {/* Breadcrumb Navigation */}
        <nav className="admin-artwork-breadcrumb">
          <button
            className="admin-artwork-breadcrumb-link"
            onClick={handleBackToArtTags}
            aria-label={`Back to ${category?.name}`}
          >
            <svg
              className="admin-artwork-breadcrumb-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {category?.name}
          </button>
          <span className="admin-artwork-breadcrumb-separator">/</span>
          <span className="admin-artwork-breadcrumb-current">
            {artTag?.name || "Loading..."}
          </span>
        </nav>

        {/* Header with Category and Tag */}
        <div className="admin-artwork-list-header">
          <h1 className="admin-artwork-list-title">
            {category?.name || "Loading..."}
          </h1>
          <div className="admin-artwork-list-tag-badge">
            <span className="admin-artwork-list-tag-label">Tag:</span>
            <span className="admin-artwork-list-tag-name">
              {artTag?.name || "Loading..."}
            </span>
          </div>
        </div>

        {error && <div className="admin-artwork-list-error">{error}</div>}

        <div className="admin-artworks-grid">
          <CreateArtworkCard
            onCreate={handleCreate}
            onAddExisting={handleAddExisting}
            categoryId={categoryId}
            tagId={tagId}
          />
          {artworks.map((artwork) => (
            <EditArtworkCard
              key={artwork._id}
              artwork={artwork}
              categoryId={categoryId}
              tagId={tagId}
              onRemove={handleRemove}
              onUpdate={handleUpdate}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      </div>

      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title="Remove Artwork from Tag?"
        message={`Are you sure you want to remove "${removeConfirm.artwork?.title}" from this tag? The artwork will not be deleted, only removed from "${artTag?.name}".`}
        confirmText="Remove"
        cancelText="Cancel"
        type="warning"
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
      />
    </div>
  );
};

export default AdminArtworkListPage;
